<template>
  <billing-dialog
    :visible.sync="visible"
    class="comparison-dialog"
    :title="$t('billing.comparison.compare_all_features')"
    title-direction="center"
    :before-close="handleBeforeClose"
    @back-event="handleBeforeClose"
  >
    <FullComparisonTable v-if="$context.is.desktop" :treeData="treeData" :colConfig="colConfig" :value="planType" />
    <PlanComparisonMobile v-else class="comparison-dialog__comparison-tree-table-mobile" :treeData="treeData" :colConfig="colConfig" :value="planType">
      <template v-if="visibleOperationColumn" #button="{ col }">
        <template v-if="!col.hideBtn">
          <i v-if="col.category === planType || col.selected" class="zm-icon-ok no-event-btn" tabindex="-1" :aria-label="col.title + ' Selected'" />
          <zm-tooltip v-else-if="col.btn && col.btn.disabledTips" :disabled="!col.btn.disabled">
            <p slot="content" v-html="col.btn.disabledTips" @click="onSubmitFormClick(col, $event)" />
            <div>
              <zm-button :type="col.btn.type" round :disabled="col.btn.disabled" tabindex="-1" :aria-label="col.btn.ariaLabel" @click="col.btn.callback(col)">{{ col.btn.label }}</zm-button>
            </div>
          </zm-tooltip>
          <zm-button v-else-if="col.btn" :type="col.btn.type" round :disabled="col.btn.disabled" tabindex="-1" :aria-label="col.btn.ariaLabel" @click="col.btn.callback(col)">{{ col.btn.label }}</zm-button>
          <div v-else class="empty-btn" aria-hidden="true" />
        </template>
      </template>
    </PlanComparisonMobile>
  </billing-dialog>
</template>
<script type="text/jsx">
import { get, some } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import {
  planTypes,
} from '@/common/constants';
import { getDefaultFlowType } from '@/common/service/page-config';
import ComparisonDataMixins from './full-comparison-dialog';
import FullComparisonTable from '@/components/full-comparison-table/full-comparison-table.vue';
import UsageTypeMixins from '@/views/checkout/address/mx-usage-type';
import { isBizPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { PLAN_COMPARISON_MOBILE as PlanComparisonMobile } from '@zoom/billing-shared';
import { updateHrefWithQueryParams } from '@/utils';
import {  generateCartIdWithQueryKey } from "@/views/config/shopping-cart/share-link.vue";
import { navigateParentWindowTo } from '@/utils/modal-utils.js';
import BillingDialog from '@/components/billing-dialog.vue';

export default {
  name: 'FullComparisonDialog',
  mixins: [ConfirmComponent, ComparisonDataMixins, UsageTypeMixins],
  props: {
    planType: String
  },
  components: {
    BillingDialog,
    FullComparisonTable,
    PlanComparisonMobile,
  },
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
      allPlanMap: state => state.ShoppingCart.allPlanMap
    }),
    ...mapGetters([
      'getCurrentBasePlanInfo',
    ]),
    hideBtn() {
      return this.$context.is.add2cart || this.$context.is.edit || this.$context.is.add2trial || this.$context.is.trial2paid || this.$context.is.zs2ze || this.$context.is.switch;
    },
    canNotBuyZoomSaas() {
      const disablePlanCategoryList = get(this.allPlanMap, 'disablePlanCategoryList', []);
      return disablePlanCategoryList.includes('ZET');
    },
    colConfig() {
      return [
        {
          title: 'Zoom Webinars',
          key: 'webinar',
          category: planTypes.WEBINAR,
          visible: true,
          hideBtn: this.hideBtn,
          btn: {
            callback: this.handleSwitchMeetingPlan.bind(this),
            label: this.$t('billing.common.select'),
            disabled: this.$context.is.isFreeWinback,
            link: '/',
            type: 'primary'
          },
        },
        {
          title: 'Zoom Sessions',
          key: 'sessions',
          category: planTypes.EVENT_SAAS_CORE,
          hideBtn: this.hideBtn,
          visible: true,
          btn: {
            callback: this.handleSwitchMeetingPlan.bind(this),
            label: this.$t('billing.common.select'),
            disabled: this.canNotBuyZoomSaas || this.$context.is.isFreeWinback,
            disabledTips: this.$t('billing.error.can_not_bought_plan_tips'),
            link: '/',
            type: 'primary'
          },
        },
        {
          title: 'Zoom Events',
          key: 'events',
          category: planTypes.EVENT_SAAS,
          hideBtn: this.hideBtn,
          visible: true,
          btn: {
            callback: this.handleSwitchMeetingPlan.bind(this),
            label: this.$t('billing.common.select'),
            disabled: this.canNotBuyZoomSaas || this.$context.is.isFreeWinback,
            disabledTips: this.$t('billing.error.can_not_bought_plan_tips'),
            link: '/',
            type: 'primary'
          },
        },
      ].filter(v => v.visible);
    },
    visibleOperationColumn() {
      return some(this.colConfig, (x) => !x.hideBtn);
    },
    basePlan() {
      return get(this.getCurrentBasePlanInfo, 'plans.0', {});
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        v && this.$tracking.buyInteraction('product details',
          'full plan comparison - impression');
      }
    }
  },
  methods: {
    handleBeforeClose() {
      this.$tracking.buyInteraction('product details', 'full plan comparison - exit');
      this.handleCancel();
    },
    handleRowClick(row) {
      if (row.subTitle) {
        const featureCol = document.querySelector(`.${row.id}-sub-title`);
        let cel = null;
        let expandBtn = null;
        if (featureCol) {
          cel = featureCol.parentElement;
        }
        if (cel) {
          expandBtn = cel.querySelector('.zm-table__expand-icon');
        }
        if (expandBtn) {
          expandBtn.click();
        }
      }
    },
    arraySpanMethod({ row, columnIndex }) {
      if (row.subTitle) {
        return columnIndex === 0 ? [1, 10] : [0, 0];
      }
    },
    handleSwitchMeetingPlan(col) {
      if (col?.btn?.disabled) return;

      this.$tracking.buyInteraction('product details', `full plan comparison select - ${col?.category}`);

      switch (col.category) {
        case planTypes.WEBINAR:
          return this.switchWebinar(col);
        case planTypes.EVENT_SAAS:
          return this.switchZE(col);
        case planTypes.EVENT_SAAS_CORE:
          return this.switchZS(col);
      }
    },
    switchWebinar() {
      const planMap = this.planMapFlat.filter(p => p.type === planTypes.WEBINAR);
      this.jump({ plan: this.basePlan.category, period: this.basePlan.billing_cycle, from: get(planMap, 'category.0', 'webinar500') });
    },
    switchZE() {
      const planMap = this.planMapFlat.filter(p => p.type === planTypes.EVENT_SAAS);
      this.jump({ plan: this.basePlan.category, period: this.basePlan.billing_cycle, from: get(planMap, 'category.0', 'eventsaas100') });
    },
    switchZS() {
      const planMap = this.planMapFlat.filter(p => p.type === planTypes.EVENT_SAAS_CORE);
      this.jump({ plan: this.basePlan.category, period: this.basePlan.billing_cycle, from: get(planMap, 'category.0', 'eventsaascore100') });
    },
    jump({ plan = 'pro', period = 'annual', from = '' }) {
      const usageType = this.initUsageType();
      const type = getDefaultFlowType();
      isBizPlusContext && (plan = planTypes.ZOOM_ONE_BUNDLE);
      let href = `/opc/buy/config?plan=${plan}&period=${period}&from=${from}&usageType=${usageType}`;
      href = type ? href + `&type=${type}` : href;
      // console.log(updateHrefWithQueryParams(href, [generateCartIdWithQueryKey]));
      if (this.$context.is.modal) {
        navigateParentWindowTo(updateHrefWithQueryParams(href, [generateCartIdWithQueryKey]));
      } else {
        location.href = updateHrefWithQueryParams(href, [generateCartIdWithQueryKey]);
      }
    }
  },
}
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.comparison-dialog {
  .zm-dialog {
    max-width: 1000px;
    width: auto;
  }

  &__title {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 700;
    font-size: 22px;
    line-height: 150%;
    color: #00053d;
    text-align: center;
  }
  &__comparison-tree-table-mobile {
    .button-area {
      box-sizing: content-box;
      padding-top: 12px;
      height: 38px;
    }
  }
}
</style>
